import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Form, Spinner} from "react-bootstrap";
import {CLEAR_CONFIRMATION, clearRsoTestData, countsText, readRsoTestData} from "./useRsoTestData";
import RsoSignInExpired, {isSignInExpired} from "./RsoSignInExpired";
import "./RsoTestDataPanel.css";

// The bottom of the Admin page's "RSO" pop-up, on test environments only: empty every RSO record so
// Sales testing can start again. NANCY says whether this environment and account may (allowed);
// everywhere else, production included, this renders nothing. Typing CLEAR arms the button.
// onBusyChange(busy): a clear is running, so the pop-up stays open until NANCY answers.
export default function RsoTestDataPanel({token, onBusyChange}) {
  const [state, setState] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [signInExpired, setSignInExpired] = useState(false);
  const [notice, setNotice] = useState("");
  const alive = useRef(true);
  useEffect(() => {alive.current = true; return () => {alive.current = false;};}, []);
  useEffect(() => {if (onBusyChange) onBusyChange(busy);}, [busy, onBusyChange]);

  useEffect(() => {
    if (!token) return undefined;
    let current = true;
    readRsoTestData(token).then((value) => {if (current) setState(value);})
      .catch(() => {if (current) setState(null);});
    return () => {current = false;};
  }, [token, attempt]);

  if (!state?.allowed) return null;

  const clear = async () => {
    if (busy || typed.trim() !== CLEAR_CONFIRMATION) return;
    setBusy(true);setError("");setNotice("");setSignInExpired(false);
    try {
      const cleared = await clearRsoTestData(token);
      if (!alive.current) return;
      setNotice(`RSO data cleared: ${countsText(cleared)} removed. Sales should reload their page.`);
      setConfirming(false);setTyped("");
      setAttempt((value) => value + 1);
    } catch (failure) {
      if (!alive.current) return;
      if (isSignInExpired(failure)) setSignInExpired(true);
      else setError(failure?.response?.data?.message || "RSO data could not be cleared. Nothing was changed. Try again.");
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const empty = state.counts.totalRows === 0;
  return <section className="rtd" aria-label="Clear all RSO data">
    <h6 className="rsw-divider">Clear all RSO data <span className="rtd-tag">test environments only</span></h6>
    <ul className="rtd-facts">
      <li>Removes every RSO record Sales made: material changes, monthly forecasts and No units answers, drafts,
        and saved floor-plan materials. Every property goes back to its floor-plan materials.</li>
      <li>Keeps RSO on or off and the input window.</li>
      <li>Then clear Forecast right away with its own Clear all RSO data, before its next Refresh: until then
        Forecast keeps the requests Purchasing already imported.</li>
      <li>This can&apos;t be undone.</li>
    </ul>
    <p className="rtd-counts">{empty ? "Nothing to clear: no RSO records on file." : `On file now: ${countsText(state.counts)}.`}</p>
    {!confirming
      ? <Button type="button" variant="outline-danger" className="rtd-open" disabled={busy || empty}
        onClick={() => {setConfirming(true);setTyped("");setError("");setNotice("");}}>Clear all RSO data…</Button>
      : <div className="rtd-confirm">
        <Form.Label htmlFor="rtd-confirm-input">Type CLEAR to confirm</Form.Label>
        <Form.Control id="rtd-confirm-input" type="text" value={typed} disabled={busy} autoComplete="off" spellCheck={false}
          onChange={(event) => setTyped(event.target.value)} />
        <div className="rtd-actions">
          <Button type="button" variant="outline-secondary" disabled={busy} onClick={() => {setConfirming(false);setTyped("");}}>Cancel</Button>
          <Button type="button" variant="danger" disabled={busy || typed.trim() !== CLEAR_CONFIRMATION} onClick={clear}>
            {busy ? <><Spinner as="span" animation="border" size="sm" aria-hidden="true" /> Clearing…</> : "Clear all RSO data"}
          </Button>
        </div>
      </div>}
    {signInExpired ? <RsoSignInExpired className="rsw-message" />
      : error && <Alert variant="danger" className="rsw-message" role="alert">{error}</Alert>}
    {notice && <p className="rsw-notice" role="status">{notice}</p>}
  </section>;
}
